import { useState } from "react";
import { LifeBuoy, RefreshCw, MonitorPlay, Monitor, Wifi, FolderOpen, FolderX, MoreHorizontal, Plus } from "lucide-react";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@/components/ui/context-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { groupActions, useGroupEntries } from "@/components/GroupColumn";
import { Count, Section2, itemCls, useFold } from "@/components/sv2/Sidebar2";

const ICONS = { help: LifeBuoy, outdated: RefreshCw, sessions: MonitorPlay, all: Monitor, online: Wifi, none: FolderX };
const LABELS = { outdated: "À mettre à jour" };

// One view or client in the dark sidebar, with the same right-click / "•••" actions as ScreenView 1.
const Entry = ({ e, i, total, admin, act, active, onClick, mini, fold }) => {
  const items = groupActions(e, i, total, admin, act);
  const Icon = ICONS[e.sel] || FolderOpen;
  const n = typeof e.n === "number" ? e.n : e.count;
  const warn = e.sel === "help" || e.sel === "outdated";
  const label = LABELS[e.sel] || e.label;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div onClick={onClick} title={label} data-testid={e.id} className={`group/e ${itemCls(active, mini)}`}>
          <Icon className={mini ? "h-[18px] w-[18px]" : `h-4 w-4 ${active ? "text-sky-300" : "opacity-60"}`} />
          {!mini && <span className="flex-1 truncate">{label}</span>}
          {!mini && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button onClick={(ev) => ev.stopPropagation()} data-testid={`${e.id}-more`} className="hidden group-hover/e:flex data-[state=open]:flex h-5 w-5 rounded hover:bg-white/10 items-center justify-center"><MoreHorizontal className="h-4 w-4" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                {items.map(([k, lbl, fn, ok]) => <DropdownMenuItem key={k} disabled={!ok} onSelect={fn} data-testid={`${e.id}-menu-${k}`}>{lbl}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!mini && fold}
          {(!mini || warn) && <Count n={n} warn={warn} mini={mini} />}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-60" data-testid={`${e.id}-context-menu`}>
        {items.map(([k, lbl, fn, ok]) => <ContextMenuItem key={k} disabled={!ok} onSelect={fn} data-testid={`${e.id}-ctx-${k}`}>{lbl}</ContextMenuItem>)}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const AccessSide2 = ({ mini, selected, onSelect, ...rest }) => {
  const { user } = useAuth();
  const { entries, act, add } = useGroupEntries({ ...rest, onSelect });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [viewsFolded, viewsFold] = useFold("views");
  const admin = user?.role === "admin";
  const views = entries.filter((e) => !e.g);
  const clients = entries.filter((e) => e.g);
  const row = (e, k, folded, fold) => (k === 0 || !folded || mini) && (
    <Entry key={e.key} e={e} i={entries.indexOf(e)} total={entries.length} admin={admin} act={act} active={selected === e.sel} onClick={() => onSelect(e.sel)} mini={mini} fold={k === 0 && fold} />
  );
  const submit = async (ev) => { ev.preventDefault(); if (name.trim() && (await add(name.trim()))) { setName(""); setAdding(false); } };
  return (
    <>
      <Section2 id="views" title="Vues" mini={mini}>{views.map((e, k) => row(e, k, viewsFolded, viewsFold))}</Section2>
      <Section2 id="clients" title="Clients" mini={mini}
        extra={!adding && <button onClick={() => setAdding(true)} title="Nouveau client" data-testid="create-group-btn" className="hover:text-white"><Plus className="h-3.5 w-3.5" /></button>}>
        {adding && !mini && (
          <form onSubmit={submit} className="mx-2 mb-1 flex gap-1">
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={() => !name && setAdding(false)} placeholder="Nom de l'entreprise" data-testid="group-name-input"
              className="flex-1 min-w-0 rounded-md bg-white/10 px-2 h-8 text-[13px] text-white placeholder:text-white/40 outline-none focus:ring-1 focus:ring-sky-400" />
            <button type="submit" disabled={!name.trim()} data-testid="group-add-btn" className="rounded-md bg-sky-500 px-2 text-[12px] font-semibold disabled:opacity-40">OK</button>
          </form>
        )}
        {clients.map((e, k) => row(e, k, false, null))}
        {!mini && !clients.length && <p className="px-4 py-1 text-[12px] text-white/40">Aucun client : cliquez sur +</p>}
      </Section2>
    </>
  );
};
